import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, InlineStack, Text } from '@shopify/polaris';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../services/api';
import { useScanContext } from '../providers/ScanContext';
import { useScanStatus } from '../hooks/useScanStatus';
import type { ScanProgress } from '../types';
import { formatDuration } from '../utils/dateFormat';

/**
 * T066: RescanButton component.
 * Rescan button with daily limit display and cooldown countdown.
 */

interface RescanButtonProps {
  /** 'store' for full store rescan, or product ID for single-product rescan */
  target: string;
  /** Current store tier */
  tier?: string;
  /** Optional callback when scan is triggered */
  onScanTriggered?: (scanProgress: ScanProgress) => void;
}

export function RescanButton({ target, tier = 'FREE', onScanTriggered }: Readonly<RescanButtonProps>) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setActiveJobId } = useScanContext();
  const [cooldownEnd, setCooldownEnd] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<string | null>(null);
  const isProductRescan = target !== 'store';

  // For single-product rescans: track the job ID and poll for completion
  const [productJobId, setProductJobId] = useState<string | null>(null);
  const { data: jobProgress } = useScanStatus(productJobId);

  // Show toast helper
  const showToast = useCallback((message: string, isError = false) => {
    const shopify = (globalThis as any).shopify;
    if (typeof shopify?.toast?.show === 'function') {
      shopify.toast.show(message, { isError });
    }
  }, []);

  // React to single-product job completion/failure
  useEffect(() => {
    if (!productJobId || !jobProgress) return;

    if (jobProgress.status === 'COMPLETED') {
      showToast('Product rescanned successfully');
      setProductJobId(null);
      // Refresh product data
      queryClient.invalidateQueries({ queryKey: ['product', target] });
      queryClient.invalidateQueries({ queryKey: ['score-history', target] });
      queryClient.invalidateQueries({ queryKey: ['fix-history', target] });
      queryClient.invalidateQueries({ queryKey: ['store-score'] });
    } else if (jobProgress.status === 'FAILED') {
      showToast(jobProgress.errorMessage ?? 'Product rescan failed', true);
      setProductJobId(null);
    } else if (jobProgress.status === 'CANCELLED') {
      showToast('Product rescan was cancelled', true);
      setProductJobId(null);
    }
  }, [productJobId, jobProgress, queryClient, target, showToast]);

  const { mutate, isPending } = useMutation<ScanProgress, ApiError>({
    mutationFn: () => {
      if (target === 'store') {
        return api.post<ScanProgress>('/scan/store');
      }
      return api.post<ScanProgress>(`/scan/product/${target}`);
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      queryClient.invalidateQueries({ queryKey: ['product', target] });
      onScanTriggered?.(data);

      if (isProductRescan && data.jobId) {
        // Single-product: stay on page, poll via useScanStatus, show toast
        showToast('Rescanning product…');
        setProductJobId(data.jobId);
      } else if (data.jobId) {
        // Store rescan: navigate to scan progress page
        setActiveJobId(data.jobId);
        navigate(`/scan-progress/${data.jobId}`);
      }
    },
    onError: (error) => {
      if (error.status === 429) {
        // Parse nextScanAvailableAt from error
        try {
          const details = JSON.parse(error.message);
          if (details?.details?.nextScanAvailableAt) {
            setCooldownEnd(new Date(details.details.nextScanAvailableAt));
          }
        } catch {
          // Set generic 24h cooldown
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(0, 0, 0, 0);
          setCooldownEnd(tomorrow);
        }
        showToast('Daily scan limit reached. Try again tomorrow.', true);
      } else {
        showToast(error.message ?? 'Failed to start rescan', true);
      }
    },
  });

  // Countdown timer
  useEffect(() => {
    if (!cooldownEnd) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = cooldownEnd.getTime() - now.getTime();

      if (diff <= 0) {
        setCooldownEnd(null);
        setCountdown(null);
        return;
      }

      setCountdown(formatDuration(diff));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [cooldownEnd]);

  const isOnCooldown = cooldownEnd !== null;
  const isFree = tier === 'FREE';
  const isScanning = isPending || productJobId !== null;
  // Per-product rescan is not available on the free tier
  const isProductRescanBlocked = isProductRescan && isFree;

  const defaultLabel = target === 'store' ? 'Rescan Store' : 'Rescan Product';
  const buttonLabel = (isScanning && isProductRescan) ? 'Scanning…' : defaultLabel;

  return (
    <InlineStack gap="200" blockAlign="center">
      <Button
        onClick={() => mutate()}
        loading={isScanning}
        disabled={isOnCooldown || isProductRescanBlocked}
        size="slim"
      >
        {buttonLabel}
      </Button>
      {isOnCooldown && countdown && (
        <Text as="span" variant="bodySm" tone="subdued">
          Next scan in {countdown}
        </Text>
      )}
      {isProductRescanBlocked && (
        <Text as="span" variant="bodySm" tone="subdued">
          Not available on Free plan
        </Text>
      )}
      {isFree && !isOnCooldown && !isProductRescanBlocked && (
        <Text as="span" variant="bodySm" tone="subdued">
          1 scan/day on Free plan
        </Text>
      )}
    </InlineStack>
  );
}
